/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================
*/

import React, { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
} from "react-table";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

import DataTableHeadCell from "common/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "common/Tables/DataTable/DataTableBodyCell";

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  searchResult = () => {},
  manualPagination = false,
  pageCount: controlledPageCount = 0,
  currentPage = 0,
  onPageChange,
  onPageSizeChange,
}) {
  const defaultValue = entriesPerPage?.defaultValue ?? 10;

  const entries = entriesPerPage?.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["5", "10", "15", "20", "25", "100"];

  const columns = useMemo(() => table?.columns || [], [table?.columns]);
  const data = useMemo(() => table?.rows || [], [table?.rows]);

  const globalFilterFn = useMemo(
    () => (rows, columnIds, filterValue) => {
      const q = String(filterValue ?? "").trim().toLowerCase();

      if (!q) return rows;

      return rows.filter((row) =>
        columnIds.some((columnId) => {
          const cellValue = row.values[columnId];

          if (typeof cellValue === "string" || typeof cellValue === "number") {
            return String(cellValue).toLowerCase().includes(q);
          }

          return false;
        })
      );
    },
    []
  );

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: defaultValue,
      },
      autoResetPage: false,
      autoResetSortBy: false,
      autoResetGlobalFilter: false,
      globalFilter: globalFilterFn,
      ...(manualPagination && { manualPagination: true, pageCount: controlledPageCount }),
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  useEffect(() => {
    if (manualPagination) {
      gotoPage(currentPage);
    }
  }, [currentPage]);

  const setEntriesPerPage = (value) => {
    const nextValue = Number(value);
    setPageSize(nextValue);
    gotoPage(0);
    if (manualPagination && onPageSizeChange) onPageSizeChange(nextValue);
  };

  const handleGotoPage = (index) => {
    gotoPage(index);
    if (manualPagination && onPageChange) onPageChange(index);
  };

  const [search, setSearch] = useState(globalFilter ?? "");

  useEffect(() => {
    setSearch(globalFilter ?? "");
  }, [globalFilter]);

  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
    gotoPage(0);
  }, 150);

  const onExternalSearchChange = useAsyncDebounce((value) => {
    searchResult(value);
  }, 150);

  const setSortedValue = (column) => {
    if (!isSorted) return false;
    if (!column.isSorted) return "none";
    return column.isSortedDesc ? "desc" : "asce";
  };

  const entriesStart = rows.length === 0 ? 0 : pageIndex * pageSize + 1;
  const entriesEnd = rows.length === 0 ? 0 : Math.min((pageIndex + 1) * pageSize, rows.length);

  const shouldUseInputPagination = pageOptions.length > 6;

  const renderPagination = useMemo(() => {
    if (shouldUseInputPagination) return null;

    return pageOptions.map((option) => (
      <MDPagination
        item
        key={option}
        onClick={() => handleGotoPage(option)}
        active={pageIndex === option}
      >
        {option + 1}
      </MDPagination>
    ));
  }, [shouldUseInputPagination, pageOptions, handleGotoPage, pageIndex]);

  const customizedPageOptions = useMemo(
    () => pageOptions.map((option) => option + 1),
    [pageOptions]
  );

  const handleInputPaginationValue = ({ target: { value } }) => {
    const pageNumber = Number(value);

    if (!pageNumber || pageNumber < 1) {
      handleGotoPage(0);
      return;
    }

    if (pageNumber > customizedPageOptions.length) {
      handleGotoPage(customizedPageOptions.length - 1);
      return;
    }

    handleGotoPage(pageNumber - 1);
  };

  return (
    <TableContainer sx={{ boxShadow: "none" }}>
      {entriesPerPage || canSearch ? (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  event?.preventDefault();
                  setEntriesPerPage(newValue);
                }}
                size="small"
                sx={{ width: "5rem" }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;entries per page
              </MDTypography>
            </MDBox>
          )}

          {canSearch && (
            <MDBox width="12rem" ml="auto">
              <MDInput
                placeholder="Search..."
                value={search}
                size="small"
                fullWidth
                onChange={({ currentTarget }) => {
                  const value = currentTarget.value;
                  setSearch(value);
                  onSearchChange(value);
                  onExternalSearchChange(value);
                }}
              />
            </MDBox>
          )}
        </MDBox>
      ) : null}

      <Table {...getTableProps()}>
        <MDBox component="thead">
          {headerGroups.map((headerGroup) => {
            const hg = headerGroup.getHeaderGroupProps();
            const { key: hgKey, ...hgProps } = hg;

            return (
              <TableRow key={hgKey} {...hgProps}>
                {headerGroup.headers.map((column) => {
                  const sortProps = isSorted ? column.getSortByToggleProps() : {};
                  const hp = column.getHeaderProps(sortProps);
                  const { key: colKey, ...colProps } = hp;

                  return (
                    <DataTableHeadCell
                      key={`${column.id}-${colKey}`}
                      {...colProps}
                      width={column.width ?? "auto"}
                      align={column.align ?? "left"}
                      sorted={setSortedValue(column)}
                    >
                      {column.render("Header")}
                    </DataTableHeadCell>
                  );
                })}
              </TableRow>
            );
          })}
        </MDBox>

        <TableBody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            const rg = row.getRowProps();
            const { key: rowKey, ...rowProps } = rg;

            return (
              <TableRow key={rowKey} {...rowProps}>
                {row.cells.map((cell) => {
                  const cg = cell.getCellProps();
                  const { key: cellKey, ...cellProps } = cg;

                  return (
                    <DataTableBodyCell
                      key={cellKey}
                      noBorder={noEndBorder && page.length - 1 === row.index}
                      align={cell.column.align ?? "left"}
                      {...cellProps}
                    >
                      {cell.render("Cell")}
                    </DataTableBodyCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {rows.length} entries
            </MDTypography>
          </MDBox>
        )}

        {pageOptions.length > 1 && (
          <MDPagination
            variant={pagination?.variant || "gradient"}
            color={pagination?.color || "info"}
          >
            {canPreviousPage && (
              <MDPagination item onClick={() => handleGotoPage(pageIndex - 1)}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
              </MDPagination>
            )}

            {shouldUseInputPagination ? (
              <MDBox width="5rem" mx={1}>
                <MDInput
                  inputProps={{
                    type: "number",
                    min: 1,
                    max: customizedPageOptions.length,
                  }}
                  value={customizedPageOptions[pageIndex] || 1}
                  onChange={handleInputPaginationValue}
                />
              </MDBox>
            ) : (
              renderPagination
            )}

            {canNextPage && (
              <MDPagination item onClick={() => handleGotoPage(pageIndex + 1)}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
              </MDPagination>
            )}
          </MDPagination>
        )}
      </MDBox>
    </TableContainer>
  );
}

DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.shape({
    columns: PropTypes.array,
    rows: PropTypes.array,
  }).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  searchResult: PropTypes.func,
  manualPagination: PropTypes.bool,
  pageCount: PropTypes.number,
  currentPage: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
};

export default React.memo(DataTable);